import { useMemo, useState } from 'react';

import createSafeContext from '#/utils/createSafeContext';

import {
  INITIAL_STATE,
  UsernameFormState,
} from './username-form-context-state';
import { UsernameFormView, createView } from './username-form-context-view';
import {
  UsernameFormActions,
  createActions,
} from './username-form-context-actions';
import { useSubmit } from './username-form-context-service';
import { useAccountProfileState } from './account-profile-context';

export const [useUsernameFormView, ViewProvider] =
  createSafeContext<UsernameFormView>('UsernameFormView');

export const [useUsernameFormActions, ActionsProvider] =
  createSafeContext<UsernameFormActions>('UsernameFormActions');

interface ProviderProps {
  readonly children: React.ReactNode;
}

export function UsernameFormProvider(props: ProviderProps) {
  const initialState = useInitialState();
  const [state, setState] = useState(initialState);

  const view = useMemo(() => createView(state), [state]);

  const submit = useSubmit(view);
  const service = useMemo(() => ({ submit }), [submit]);

  const actions = useMemo(
    () => createActions(setState, service),
    [setState, service],
  );

  return (
    <ActionsProvider value={actions}>
      <ViewProvider value={view}>{props.children}</ViewProvider>
    </ActionsProvider>
  );
}

/**
 * Initial state augmented with values
 * from the account context state.
 */
function useInitialState(): UsernameFormState {
  const accountState = useAccountProfileState();

  const username =
    accountState.profile?.username ?? INITIAL_STATE.values.username;
  const isUsernamePrivate =
    accountState.profile?.is_username_private ??
    INITIAL_STATE.values.isUsernamePrivate;

  return {
    ...INITIAL_STATE,
    values: {
      ...INITIAL_STATE.values,
      username,
      isUsernamePrivate,
    },
  };
}
